import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  navigationItems,
  NAVIGATION_SECTIONS,
} from "@/lib/constants/navigation";
import { SidebarNavItem } from "./SidebarNavItem";
import { normalizePermissions } from "@/helpers/permissionUtils";
import { filterNavigationItems } from "@/helpers/navigationFilter";
import { useAuthStore } from "@/features/auth/hooks";

const Sidebar = observer(
  ({ collapsed, onCollapseChange, className = "", mobile = false }) => {
    const { t } = useTran();
    const { user } = useAuthStore();

    const allowedModules = normalizePermissions(user?.permissions || []);
    const validMenus = filterNavigationItems(navigationItems, allowedModules, {
      userRole: user?.role,
      companyFeatures: { customFrameworkEnabled: user?.customFrameworkEnabled },
    });

    // Group navigation items by section
    const groupedSections = Object.values(NAVIGATION_SECTIONS)
      .map((section) => ({
        title: section,
        items: validMenus.filter((item) => item.section === section),
      }))
      .filter((group) => group.items.length > 0);

    return (
      <aside
        className={cn(
          "flex h-full min-h-0 flex-col overflow-visible border-r text-sm transition-[width] duration-200",
          "bg-card text-card-foreground border-border",
          mobile
            ? "w-full max-w-none"
            : collapsed
              ? "w-[64px]"
              : "w-full max-w-[280px] xl:max-w-[300px]",
          className,
        )}
      >
        {/* Header with Brand and Collapse Toggle */}
        <div className="relative h-14 border-b border-border px-4 py-2">
          <div className="flex h-full items-center">
            {!collapsed && (
              <Link to="/" className="flex min-w-0 items-center">
                <img
                  src="/logos/logo.svg"
                  alt={t("NxStart Logo")}
                  className="h-12 w-auto"
                />
              </Link>
            )}
          </div>

          {/* Collapse Toggle Button */}
          {!mobile ? (
            <button
              type="button"
              onClick={() => onCollapseChange?.(!collapsed)}
              className="absolute top-1/2 z-[50] flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm ring-1 ring-background transition-colors hover:border-border hover:bg-muted"
              style={{ right: "-14px" }}
              aria-label={
                collapsed ? t("Expand sidebar") : t("Collapse sidebar")
              }
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4 text-current" />
              ) : (
                <ChevronLeft className="h-4 w-4 text-current" />
              )}
            </button>
          ) : null}
        </div>

        {/* Navigation */}
        {collapsed ? (
          <nav className="flex w-[64px] flex-1 flex-col items-center overflow-y-auto px-2 py-5">
            <div className="flex flex-col items-center gap-2">
              {validMenus.map((item) => (
                <SidebarNavItem
                  key={item.label}
                  item={item}
                  collapsed={collapsed}
                />
              ))}
            </div>
          </nav>
        ) : (
          <nav className="flex-1 overflow-y-auto px-3 py-4 sm:px-4 sm:py-5">
            <div className="space-y-6">
              {groupedSections.map(({ title, items }) => (
                <div key={title} className="space-y-2">
                  <p className="text-[11px] font-semibold uppercase text-muted-foreground">
                    {t(title)}
                  </p>
                  <div className="space-y-1">
                    {items.map((item) => (
                      <SidebarNavItem
                        key={item.label}
                        item={item}
                        collapsed={collapsed}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </nav>
        )}
      </aside>
    );
  },
);

export default Sidebar;
